import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X, FileText, Users, Package, Tag } from 'lucide-react'
import axios from 'axios'

interface SearchResult {
  query: string
  total: number
  pratiche: Array<{
    id: number
    numero_progressivo: string
    titolo: string | null
    stato: string
    tipologia: string | null
    codice_sito: string | null
    denominazione_sito: string | null
    data_appuntamento: string | null
  }>
  clienti: Array<{
    id: number
    nome_display: string
    comune: string | null
    codice_sito: string | null
    denominazione_sito: string | null
    telefono: string | null
  }>
  apparati: Array<{
    id: number
    seriale: string
    modello: string | null
    marca: string | null
    stato: string | null
  }>
  articoli: Array<{
    id: number
    codice: string
    descrizione: string | null
    categoria: string | null
  }>
}

const STATO_COLORS: Record<string, string> = {
  ricevuto: '#3b82f6',
  assegnato: '#f59e0b',
  eseguito: '#15803d',
  sospeso: '#6b7280',
  annullato: '#dc2626',
}

export default function GlobalSearch() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const search = useCallback(async (q: string) => {
    if (q.length < 2) { setResults(null); setOpen(false); return }
    setLoading(true)
    try {
      const token = localStorage.getItem('xsophia_token')
      const res = await axios.get('/api/search', {
        params: { q, limit: 5 },
        headers: { Authorization: `Bearer ${token}` },
      })
      setResults(res.data)
      setOpen(true)
    } catch {
      setResults(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => search(query), 280)
    return () => { if (timerRef.current) clearTimeout(timerRef.current) }
  }, [query, search])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
      }
      if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const go = (path: string) => {
    setOpen(false)
    setQuery('')
    setResults(null)
    navigate(path)
  }

  const hasResults = results && results.total > 0

  return (
    <div ref={containerRef} style={{ position: 'relative', flexShrink: 0 }}>
      {/* Input */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'rgba(0,0,0,0.05)',
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: 8,
        padding: '0 10px',
        height: 30,
        width: open || query ? 220 : 180,
        transition: 'width 0.2s ease',
      }}>
        {loading
          ? <div style={{ width: 13, height: 13, border: '1.5px solid #1E40AF', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />
          : <Search size={13} style={{ color: '#64748b', flexShrink: 0 }} />
        }
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => { if (results && results.total > 0) setOpen(true) }}
          placeholder="Cerca… (⌘K)"
          style={{
            flex: 1, border: 'none', background: 'transparent', outline: 'none',
            fontSize: 12, color: '#111827', minWidth: 0,
          }}
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setResults(null); setOpen(false) }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: '#94a3b8' }}
          >
            <X size={12} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 36, right: 0, zIndex: 9999,
          background: '#fff', borderRadius: 12,
          width: 340, maxWidth: 'calc(100vw - 24px)',
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb',
          overflow: 'hidden', maxHeight: 480, overflowY: 'auto',
        }}>
          {!hasResults && (
            <div style={{ padding: '20px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              Nessun risultato per "{query}"
            </div>
          )}

          {hasResults && results.pratiche.length > 0 && (
            <Section icon={<FileText size={13} />} label="Pratiche" count={results.pratiche.length}>
              {results.pratiche.map(p => (
                <ResultRow
                  key={p.id}
                  onClick={() => go(`/pratiche/${p.id}`)}
                  primary={p.numero_progressivo}
                  secondary={p.denominazione_sito ?? p.codice_sito ?? p.tipologia ?? ''}
                  badge={p.stato}
                  badgeColor={STATO_COLORS[p.stato] ?? '#6b7280'}
                />
              ))}
            </Section>
          )}

          {hasResults && results.clienti.length > 0 && (
            <Section icon={<Users size={13} />} label="Clienti" count={results.clienti.length}>
              {results.clienti.map(c => (
                <ResultRow
                  key={c.id}
                  onClick={() => go(`/clienti`)}
                  primary={c.nome_display || c.codice_sito || '—'}
                  secondary={[c.comune, c.denominazione_sito].filter(Boolean).join(' · ')}
                />
              ))}
            </Section>
          )}

          {hasResults && results.apparati.length > 0 && (
            <Section icon={<Package size={13} />} label="Apparati" count={results.apparati.length}>
              {results.apparati.map(a => (
                <ResultRow
                  key={a.id}
                  onClick={() => go(`/magazzino`)}
                  primary={a.seriale}
                  secondary={[a.marca, a.modello].filter(Boolean).join(' ')}
                  badge={a.stato ?? undefined}
                  badgeColor="#64748b"
                />
              ))}
            </Section>
          )}

          {hasResults && results.articoli.length > 0 && (
            <Section icon={<Tag size={13} />} label="Articoli" count={results.articoli.length}>
              {results.articoli.map(a => (
                <ResultRow
                  key={a.id}
                  onClick={() => go(`/magazzino`)}
                  primary={a.codice}
                  secondary={a.descrizione ?? ''}
                />
              ))}
            </Section>
          )}

          {hasResults && (
            <div style={{ padding: '8px 16px', borderTop: '1px solid #f3f4f6', fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
              {results.total} risultat{results.total === 1 ? 'o' : 'i'} trovati
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Section({ icon, label, count, children }: {
  icon: React.ReactNode
  label: string
  count: number
  children: React.ReactNode
}) {
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '8px 16px 4px',
        borderBottom: '1px solid #f8fafc',
        background: '#fafafa',
      }}>
        <span style={{ color: '#64748b' }}>{icon}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>{count}</span>
      </div>
      {children}
    </div>
  )
}

function ResultRow({ primary, secondary, badge, badgeColor, onClick }: {
  primary: string
  secondary: string
  badge?: string
  badgeColor?: string
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', padding: '9px 16px',
        background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
        borderBottom: '1px solid #f9fafb',
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = '#f0f9ff')}
      onMouseLeave={e => (e.currentTarget.style.background = 'none')}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {primary}
        </div>
        {secondary && (
          <div style={{ fontSize: 11.5, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
            {secondary}
          </div>
        )}
      </div>
      {badge && (
        <span style={{
          fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 99,
          background: `${badgeColor}18`, color: badgeColor, flexShrink: 0,
          letterSpacing: '0.02em',
        }}>
          {badge}
        </span>
      )}
    </button>
  )
}
